import { useState } from 'react'
import { EntitlementType } from '@/types'

interface EntitlementIconProps {
  entitlement: EntitlementType
  /** Single operation name shown in the tooltip */
  operation?: string
  /** Multiple operation names shown in the tooltip (takes precedence over operation) */
  operations?: string[]
  size?: 'xs' | 'sm' | 'md'
}

const SIZE_MAP = {
  xs: 'w-3.5 h-3.5',
  sm: 'w-4 h-4',
  md: 'w-5 h-5',
}

const BG_RING: Record<EntitlementType, string> = {
  FREE_L1: 'bg-gray-50 ring-gray-300',
  PREMIUM_L1: 'bg-amber-50 ring-amber-300',
  PREMIUM_L2: 'bg-amber-100 ring-amber-400',
  VERIFIED_L1: 'bg-green-50 ring-green-300',
  ULTIMATE_PICKLEBALL_LEAGUE_L1: 'bg-indigo-50 ring-indigo-300',
  RESET_MARCH_2026: 'bg-rose-50 ring-rose-300',
}

const ICON_COLOR: Record<EntitlementType, string> = {
  FREE_L1: 'text-gray-500',
  PREMIUM_L1: 'text-amber-600',
  PREMIUM_L2: 'text-amber-700',
  VERIFIED_L1: 'text-green-600',
  ULTIMATE_PICKLEBALL_LEAGUE_L1: 'text-indigo-600',
  RESET_MARCH_2026: 'text-rose-600',
}

const LABEL: Record<EntitlementType, string> = {
  FREE_L1: 'Free',
  PREMIUM_L1: 'Premium',
  PREMIUM_L2: 'Premium+',
  VERIFIED_L1: 'Verified',
  ULTIMATE_PICKLEBALL_LEAGUE_L1: 'UPL',
  RESET_MARCH_2026: 'Reset',
}

const OP_LABEL: Record<string, string> = {
  tournaments: 'Tournaments',
  leagues: 'Leagues',
  ratings: 'Ratings',
  club_play: 'Club Play',
}

/* ─── SVG icons per entitlement ─── */

/** Open circle — basic / free tier */
const FreeIcon = ({ className }: { className: string }) => (
  <svg className={className} viewBox="0 0 16 16" fill="none" stroke="currentColor">
    <circle cx="8" cy="8" r="5.5" strokeWidth="1.5" />
  </svg>
)

/** Filled star — premium */
const StarIcon = ({ className }: { className: string }) => (
  <svg className={className} viewBox="0 0 16 16" fill="currentColor">
    <path d="M8 2l1.854 3.854L14 6.472l-3 2.978.708 4.228L8 11.708l-3.708 1.97L5 9.45 2 6.472l4.146-.618L8 2z" />
  </svg>
)

/** Star with small plus — premium+ */
const StarPlusIcon = ({ className }: { className: string }) => (
  <svg className={className} viewBox="0 0 16 16" fill="currentColor">
    <path d="M7 2.5l1.5 3.116L12 6.146l-2.427 2.409.573 3.42L7 10.265 3.854 11.975l.573-3.42L2 6.146l3.5-.53L7 2.5z" />
    <path d="M13 3v3M11.5 4.5h3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" fill="none" />
  </svg>
)

/** Shield with checkmark — verified */
const ShieldCheckIcon = ({ className }: { className: string }) => (
  <svg className={className} viewBox="0 0 16 16" fill="none" stroke="currentColor">
    <path d="M8 1.5L3 3.5v4c0 3.5 2.5 5.5 5 6.5 2.5-1 5-3 5-6.5v-4L8 1.5z" strokeWidth="1.3" strokeLinejoin="round" fill="currentColor" fillOpacity="0.12" />
    <path d="M5.5 8l2 2 3-3.5" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

/** Trophy — league / UPL */
const TrophyIcon = ({ className }: { className: string }) => (
  <svg className={className} viewBox="0 0 16 16" fill="none" stroke="currentColor">
    <path d="M5 2.5h6v4.5a3 3 0 01-6 0V2.5z" strokeWidth="1.3" fill="currentColor" fillOpacity="0.12" />
    <path d="M5 4H3.5a1 1 0 00-1 1v.5A2 2 0 004.5 7.5H5M11 4h1.5a1 1 0 011 1v.5a2 2 0 01-2 2H11" strokeWidth="1.2" />
    <path d="M6.5 10v1.5h3V10M6 13h4M6.5 11.5h3" strokeWidth="1.2" strokeLinecap="round" />
  </svg>
)

/** Refresh arrow — reset */
const RefreshIcon = ({ className }: { className: string }) => (
  <svg className={className} viewBox="0 0 16 16" fill="none" stroke="currentColor">
    <path d="M3 8a5 5 0 018.54-3.54M13 8a5 5 0 01-8.54 3.54" strokeWidth="1.5" strokeLinecap="round" />
    <path d="M11 2v3h3M5 14v-3H2" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

const ICON_MAP: Record<EntitlementType, React.FC<{ className: string }>> = {
  FREE_L1: FreeIcon,
  PREMIUM_L1: StarIcon,
  PREMIUM_L2: StarPlusIcon,
  VERIFIED_L1: ShieldCheckIcon,
  ULTIMATE_PICKLEBALL_LEAGUE_L1: TrophyIcon,
  RESET_MARCH_2026: RefreshIcon,
}

export const EntitlementIcon = ({ entitlement, operation, operations, size = 'sm' }: EntitlementIconProps) => {
  const [show, setShow] = useState(false)
  const Icon = ICON_MAP[entitlement]
  if (!Icon) return null

  const sizeClass = SIZE_MAP[size]
  const bg = BG_RING[entitlement] || 'bg-gray-50 ring-gray-200'
  const color = ICON_COLOR[entitlement] || 'text-gray-500'
  const label = LABEL[entitlement] || entitlement

  // Build tooltip: prefer operations array, fall back to single operation
  const opNames = operations?.map((o) => OP_LABEL[o] || o.replace(/_/g, ' '))
    ?? (operation ? [OP_LABEL[operation] || operation.replace(/_/g, ' ')] : undefined)
  const tooltip = opNames ? `${label} (${opNames.join(', ')})` : label

  return (
    <span
      className={`relative inline-flex items-center justify-center ${sizeClass} ring-1 ${bg} cursor-default`}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      <Icon className={`${sizeClass} ${color}`} />
      {show && (
        <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-1.5 py-0.5 bg-gray-900 text-white text-[10px] whitespace-nowrap z-50 pointer-events-none">
          {tooltip}
        </span>
      )}
    </span>
  )
}

/** Renders one icon per unique entitlement, with tooltip listing all operations */
export const EntitlementIcons = ({
  entitlements,
  size = 'xs',
}: {
  entitlements: Record<string, EntitlementType[]>
  size?: 'xs' | 'sm' | 'md'
}) => {
  // Invert: entitlement -> operations[]
  const byEnt = new Map<EntitlementType, string[]>()
  for (const [op, ents] of Object.entries(entitlements)) {
    for (const ent of ents) {
      const ops = byEnt.get(ent)
      if (ops) ops.push(op)
      else byEnt.set(ent, [op])
    }
  }
  if (byEnt.size === 0) return null

  return (
    <span className="inline-flex gap-0.5 ml-1">
      {Array.from(byEnt.entries()).map(([ent, ops]) => (
        <EntitlementIcon key={ent} entitlement={ent} operations={ops} size={size} />
      ))}
    </span>
  )
}
